/**
 * **Thai is the source.** Foundations §8: Thai is the design language and
 * English inherits from it. This object defines the key set; `en.ts` and `zh.ts`
 * are typed as `Record<MessageKey, string>`, so a key added here and forgotten
 * there is a compile error rather than a blank space in front of a tourist.
 *
 * Voice (foundations §9): พูดแบบเพื่อนร้านข้างๆ. Short, colloquial, no
 * corporate-speak. Money messages are calm and absolute, never exclamation
 * marks. **Errors say what to do next, in one line** — not what went wrong.
 *
 * `{braces}` mark interpolated values. See `format.ts`.
 *
 * ## The basket is called รายการอาหาร, never ตะกร้า
 *
 * DECIDED (owner, 2026-08). The build shipped ตะกร้า — the literal "basket" of
 * every e-commerce checkout — and the design package uses **รายการอาหาร**
 * throughout: the cart screen's own title, the menu's bottom bar
 * (`ดูรายการอาหาร (2) · ฿155`) and the empty state (`ยังไม่มีรายการอาหาร`). The
 * package's word wins, because a diner at a table is looking at what they have
 * ordered, not at a shopping basket they are carrying around a shop.
 *
 * Two words, not one, and the split matters:
 *
 *   * the basket **as a place** is `รายการอาหาร` — Q5's title, the bottom bar,
 *     the empty state, "take it out of the list";
 *   * the **act of adding** is `เพิ่มรายการ` (the package's verb on the item
 *     sheet). Not `ใส่รายการอาหาร`, which is what a translation engine would
 *     produce and no Thai speaker says.
 *
 * `ตะกร้า` must not come back. If a new key needs the word, it needs one of
 * these two instead.
 *
 * **English and Chinese did not change.** This is a decision about the Thai
 * lexicon; "basket" and 购物车 are the ordinary words in their own languages and
 * carry the same meaning, so they stay as they are.
 */
export const th = {
  // --- common -------------------------------------------------------------
  // The wordmark is standalone "pokpokPOS" on every surface, permanently
  // (D-014). There is no lockup and no endorsement, so this is a product name,
  // not a translated string.
  'common.appName': 'pokpokPOS',
  // Venue leads, pokpok is present but subordinate. Body copy, small.
  'common.attribution': 'สั่งอาหารด้วย pokpokPOS',
  'common.back': 'ย้อนกลับ',
  'common.close': 'ปิด',
  'common.cancel': 'ยกเลิก',
  'common.confirm': 'ยืนยัน',
  'common.retry': 'ลองอีกครั้ง',
  'common.continue': 'ต่อไป',
  'common.done': 'เสร็จแล้ว',
  'common.ok': 'ตกลง',
  'common.loading': 'กำลังโหลด…',
  'common.askStaff': 'สอบถามพนักงานได้เลย',
  'common.table': 'โต๊ะ {label}',
  'common.viewMenu': 'ดูเมนู',
  'common.quantity': 'จำนวน',
  'common.item': 'รายการ',
  'common.items': '{count} รายการ',
  'common.language': 'ภาษา',
  'common.changeLanguage': 'เปลี่ยนภาษา',
  'common.subtotal': 'ยอดรวม',
  'common.total': 'รวมทั้งสิ้น',
  'common.discount': 'ส่วนลด',
  'common.serviceCharge': 'ค่าบริการ {rate}%',
  'common.rounding': 'ปัดเศษ',
  'common.tip': 'ทิป',
  // ADR-0009: under inclusive VAT the subtotal is gross and already contains
  // the VAT. These two strings must never both appear on one breakdown.
  'common.vatIncluded': 'รวม VAT {rate}% แล้ว',
  'common.vatAdded': 'VAT {rate}%',
  'common.free': 'ไม่คิดเพิ่ม',
  'common.noPhoto': 'ไม่มีรูป',
  'common.offline': 'ออฟไลน์',
  'common.online': 'เชื่อมต่ออยู่',
  'common.reconnecting': 'กำลังเชื่อมต่อใหม่…',
  'common.stale': 'ข้อมูลอาจไม่ใช่ล่าสุด',
  'common.soldOut': 'หมดแล้ว',
  'common.updatedAt': 'อัปเดตล่าสุด {time}',
  'common.minutesAgo': '{minutes} นาทีที่แล้ว',
  'common.justNow': 'เมื่อครู่นี้',
  'common.skipToContent': 'ข้ามไปที่เนื้อหา',

  // Why an item cannot be ordered — `UnavailabilityReason` in the contract.
  'reason.eighty_six': 'ครัวแจ้งหมด',
  'reason.out_of_stock': 'ของหมด',
  'reason.off_schedule': 'ยังไม่ถึงเวลาขาย',
  'reason.channel_hidden': 'ไม่มีในเมนูนี้',
  'reason.deactivated': 'ปิดขายอยู่',

  // --- errors -------------------------------------------------------------
  // Localised from `ErrorCode`. The server's English `message` is for operators
  // and is never shown to a guest.
  'error.generic': 'ยังทำรายการไม่ได้ ลองอีกครั้ง',
  'error.network': 'เน็ตไม่ติด ลองอีกครั้ง',
  'error.session': 'QR หมดอายุแล้ว สแกนใหม่อีกครั้ง',
  'error.forbidden': 'ทำรายการนี้ไม่ได้ สอบถามพนักงานได้เลย',
  'error.notFound': 'ไม่พบรายการนี้',
  'error.itemUnavailable': 'มีบางรายการหมดแล้ว',
  'error.rateLimited': 'กดถี่เกินไป รอสักครู่แล้วลองใหม่',
  'error.validation': 'ข้อมูลยังไม่ครบ ตรวจอีกครั้ง',
  'error.conflict': 'รายการเปลี่ยนไปแล้ว ดูรายการล่าสุดอีกครั้ง',
  // The one conflict a guest actually meets. Says what happened and where the
  // food went, rather than sending them back to a menu that is fine.
  'error.tableHasOpenBill': 'โต๊ะนี้มีออเดอร์ที่สั่งไปแล้ว ดูรายการที่สั่งได้เลย',
  'error.unavailable': 'ระบบร้านยังไม่พร้อม สอบถามพนักงานได้เลย',
  'error.notAvailableHere': 'ร้านนี้ยังไม่เปิดใช้ส่วนนี้',

  // --- Q1 · scan landing --------------------------------------------------
  'q1.welcome': 'ยินดีต้อนรับ',
  // The venue's identity must be on screen before the menu loads.
  'q1.openingSession': 'กำลังเปิดเมนู…',
  'q1.youAreAt': 'คุณอยู่ที่ {table}',
  'q1.tableUnknown': 'สั่งที่โต๊ะนี้',
  'q1.startOrdering': 'เริ่มสั่งอาหาร',
  'q1.chooseLanguage': 'เลือกภาษา',
  // Order-first is the full-service default: the kitchen fires, payment is at
  // close. Say so plainly — the guest is incurring a debt.
  'q1.orderFirstNote': 'สั่งได้เลย จ่ายทีหลังที่เคาน์เตอร์',
  // A pay-first venue has no checkout on this surface: the checkout endpoints
  // are PLACEHOLDER (TBD) in the frozen contract, so there is nothing to build.
  'q1.payFirstUnavailable': 'ร้านนี้ต้องชำระเงินก่อน สั่งกับพนักงานได้เลย',
  'q1.retryOpening': 'ลองเปิดใหม่',

  // --- Q2 · language toggle -----------------------------------------------
  'q2.title': 'ภาษา',
  // Language names in their own script, never flags (03 §Q2).
  'q2.th': 'ไทย',
  'q2.en': 'English',
  'q2.zh': '中文',
  'q2.cartKept': 'รายการอาหารที่เลือกไว้ยังอยู่ครบ',

  // --- Q3 · menu browse ---------------------------------------------------
  'q3.title': 'เมนู',
  'q3.searchLabel': 'ค้นหาเมนู',
  'q3.searchPlaceholder': 'ค้นหาชื่ออาหาร',
  'q3.searchClear': 'ล้างการค้นหา',
  'q3.searchNoResults': 'ไม่พบ “{query}”',
  'q3.searchNoResultsAction': 'ลองคำอื่น หรือเลือกจากหมวดด้านบน',
  'q3.allCategories': 'ทั้งหมด',
  // The spotlight band at the top of the menu: the dishes the merchant flagged
  // `featured`. Short — it is a heading over a scroll of cards, not a sentence.
  'q3.featuredHeading': 'แนะนำ',
  'q3.emptyCategory': 'หมวดนี้ยังไม่มีรายการ',
  'q3.loading': 'กำลังโหลดเมนู…',
  'q3.menuUnavailable': 'ยังโหลดเมนูไม่ได้',
  'q3.menuUnavailableAction': 'ลองอีกครั้ง หรือสอบถามพนักงาน',
  // A price on this channel may legitimately differ from the printed menu.
  // Say it once, quietly, so it does not read as an error.
  'q3.channelPriceNote': 'ราคานี้สำหรับสั่งผ่าน QR',
  'q3.itemJustSoldOut': '“{item}” เพิ่งหมด',
  'q3.viewCart': 'ดูรายการอาหาร',
  // The package's bottom bar reads `ดูรายการอาหาร (2) · ฿155`. The count is
  // parenthetical rather than `· {count} รายการ` because the noun is already in
  // the label — "ดูรายการอาหาร · 2 รายการ" says รายการ twice in five words. The
  // price is not in this string: Q3 and Q4 render a `Money` element after it so
  // the amount keeps tabular figures and its own formatter.
  'q3.viewCartWithCount': 'ดูรายการอาหาร ({count})',
  // Dietary and spice markers: 03 §Q3 asks for them and **the frozen contract
  // carries no field for them** — `ChannelMenuItem` has no diet, allergen or
  // spice property. These labels exist so that when a field lands nobody
  // hardcodes Thai into a component. Until then the marker row renders nothing.
  'q3.markerSpicy': 'เผ็ด',
  'q3.markerVerySpicy': 'เผ็ดมาก',
  'q3.markerVegetarian': 'มังสวิรัติ',
  'q3.markerVegan': 'วีแกน',
  'q3.markerHalal': 'ฮาลาล',
  'q3.markerContainsPork': 'มีหมู',
  'q3.markerContainsNuts': 'มีถั่ว',
  'q3.markerContainsSeafood': 'มีอาหารทะเล',

  // --- Q4 · item detail & modifiers ---------------------------------------
  'q4.description': 'รายละเอียด',
  'q4.noDescription': 'ยังไม่มีรายละเอียด',
  'q4.required': 'ต้องเลือก',
  'q4.optional': 'เลือกหรือไม่ก็ได้',
  'q4.chooseExactly': 'เลือก {count} อย่าง',
  'q4.chooseAtLeast': 'เลือกอย่างน้อย {count} อย่าง',
  'q4.chooseUpTo': 'เลือกได้ไม่เกิน {count} อย่าง',
  'q4.chooseBetween': 'เลือก {min}–{max} อย่าง',
  'q4.requiredUnmet': 'ยังเลือกไม่ครบ',
  'q4.maxReached': 'เลือกครบแล้ว',
  'q4.modifierUnavailable': 'ตัวเลือกนี้หมด',
  'q4.increase': 'เพิ่มจำนวน',
  'q4.decrease': 'ลดจำนวน',
  // The note reaches the KDS and is read at 3 metres by someone who may not read
  // the guest's language. Set the expectation honestly (03 §Q4).
  'q4.noteToKitchen': 'บอกครัว',
  // **Two examples, never one.** A single example reads as the only thing a
  // guest is allowed to ask for; the second one turns it into a pattern —
  // "leave out an ingredient" — which is the whole teaching job of a
  // placeholder. This is the package's copy verbatim, and it fits: the field is
  // 16px and full-width, so there is room for both.
  'q4.notePlaceholder': 'เช่น ไม่ใส่ผักชี ไม่เอาถั่วฝักยาว',
  'q4.noteHonesty': 'ครัวอาจอ่านไม่ทันหรืออ่านภาษาอื่นไม่ได้',
  // Whether free-text notes are allowed from guests at all is an OPEN question
  // in 03 §Q4. A venue that does not allow them shows this instead.
  'q4.noteUnavailable': 'ร้านนี้ยังไม่รับข้อความถึงครัว',
  'q4.runningPrice': 'ราคารวม',
  // The verb, not the place: `เพิ่มรายการ` (see the lexicon note at the top).
  'q4.addToCart': 'เพิ่มรายการ',
  'q4.addToCartWithPrice': 'เพิ่มรายการ · {price}',
  'q4.added': 'เพิ่มรายการแล้ว',
  // 86'd while the sheet is open. Apologetic, with a way forward — not a
  // validation error (design principle #2, and 03 §Q6).
  'q4.soldOutWhileOpen': 'ขออภัย “{item}” เพิ่งหมด',
  'q4.soldOutAction': 'เลือกอย่างอื่นได้เลย',
  'q4.backToMenu': 'กลับไปที่เมนู',

  // --- Q5 · cart ----------------------------------------------------------
  'q5.title': 'รายการอาหาร',
  'q5.empty': 'ยังไม่มีรายการอาหาร',
  'q5.emptyAction': 'ไปดูเมนู',
  'q5.remove': 'ลบ',
  'q5.removed': 'ลบ “{item}” แล้ว',
  'q5.undo': 'เอากลับ',
  'q5.edit': 'แก้ไข',
  'q5.addMore': 'สั่งเพิ่ม',
  // Group ordering: lines carry `added_by_guest_session_id`, so a table can see
  // whose is whose without anybody identifying themselves.
  'q5.yourItems': 'ของคุณ',
  'q5.tableItems': 'ของคนอื่นที่โต๊ะนี้',
  // Totals are `pending` — never green before payment (foundations §4).
  'q5.notPaidYet': 'ยังไม่ได้ชำระเงิน',
  // The bill is priced by the server, not by this phone. What the cart shows
  // before submitting is an estimate from menu prices and it says so.
  'q5.estimatedTotal': 'ยอดโดยประมาณ',
  'q5.estimateNote': 'ยอดจริงคำนวณโดยร้านตอนส่งออเดอร์',
  // The confirm sheet, which replaced the review screen on 2026-08-14. The body
  // carries the three facts the retired screen carried — how many, roughly how
  // much, and when you pay — because a sheet that only asks "are you sure" makes
  // the guest go back to find out what they are being asked about.
  'q5.confirmTitle': 'ยืนยันสั่งอาหาร?',
  'q5.confirmBody': 'ส่งรายการนี้ให้ครัวทันที {count} รายการ · ยอดโดยประมาณ {total} จ่ายทีหลังที่เคาน์เตอร์',
  'q5.confirmSend': 'ยืนยัน สั่งเลย',
  // An item 86'd since it went in the cart must be handled explicitly, never
  // dropped silently (03 §Q5).
  'q5.lineSoldOut': '“{item}” หมดแล้ว',
  'q5.lineSoldOutAction': 'เอาออกจากรายการ',
  'q5.linePriceChanged': 'ราคา “{item}” เปลี่ยนเป็น {price}',
  'q5.linePriceChangedAction': 'รับทราบ',
  // There is no venue order-limit field in the frozen contract. This string is
  // here for the day one exists; nothing sets it today.
  'q5.overVenueLimit': 'สั่งได้ไม่เกิน {limit} รายการต่อครั้ง',
  'q5.placeOrder': 'ส่งออเดอร์',
  'q5.reviewOrder': 'สั่งอาหารทันที',

  // --- Q7 · order-first submit --------------------------------------------
  'q7.title': 'ยืนยันออเดอร์',
  'q7.summary': 'สรุปรายการ',
  'q7.submit': 'ส่งให้ครัว',
  'q7.submitting': 'กำลังส่ง…',
  // The guest has now incurred a debt without paying. Unambiguous: food is
  // coming, payment is later.
  'q7.payLaterNote': 'ส่งให้ครัวเลย จ่ายทีหลังที่เคาน์เตอร์',
  'q7.sentTitle': 'ส่งให้ครัวแล้ว',
  'q7.sentBody': 'ครัวได้รับรายการแล้ว อาหารกำลังมา จ่ายทีหลังที่เคาน์เตอร์',
  // `ดูรายการที่สั่ง` since 2026-08-14 (owner directive), and it is the package's
  // own label — the prototype's menu bar reads `ดูรายการที่สั่ง` in exactly this
  // slot. One string for one destination, on all five screens that point at it:
  // the menu's top bar, the sent confirmation, the payment result, the receipt
  // and the closed notice. The screen it opens no longer shows an aggregate
  // "status" at all — it is a list of what was ordered and where each dish is —
  // so the old label had also stopped describing it.
  'q7.viewStatus': 'ดูรายการที่สั่ง',
  'q7.orderAgain': 'สั่งเพิ่ม',
  'q7.rejectedTitle': 'ร้านยังไม่รับออเดอร์นี้',
  'q7.rejectedAction': 'สอบถามพนักงานได้เลย',
  'q7.connectionLostTitle': 'ส่งไม่สำเร็จ เน็ตหลุด',
  // Three idempotency layers mean pressing send again cannot duplicate the
  // order. Say that, because the fear of double-ordering is what makes a guest
  // wave at a server instead.
  'q7.connectionLostAction': 'กดส่งอีกครั้งได้เลย ไม่ซ้ำแน่นอน',
  'q7.queued': 'ค้างอยู่ กำลังส่งให้อัตโนมัติ',
  'q7.someItemsSoldOut': 'มีบางรายการหมดแล้ว',
  'q7.backToCart': 'กลับไปแก้รายการ',
  'q7.alreadySent': 'ออเดอร์นี้ส่งไปแล้ว',

  // --- Q8 · pay-first checkout --------------------------------------------
  'q8.title': 'ชำระเงิน',
  'q8.amountDue': 'ยอดที่ต้องชำระ',
  // pokpokPOS does not render the QR and does not pick the method: there is no
  // ReservePay operation anywhere that returns an EMVCo payload, so the guest
  // pays on ReservePay's own page. Say that before the tap, not after it.
  'q8.handoffNote': 'กดแล้วจะไปที่หน้าชำระเงินของ ReservePay เลือกวิธีจ่ายที่นั่น',
  'q8.methodsNote': 'เลือกวิธีชำระเงินที่หน้าถัดไป',
  // Honest about whose screen the guest is about to be on. Body copy, small,
  // nowhere near the wordmark — a processor disclosure, never a lockup (D-014).
  'q8.leavingNote': 'หน้าชำระเงินเป็นของ ReservePay ไม่ใช่หน้าของร้าน',
  'q8.pay': 'ไปหน้าชำระเงิน',
  'q8.paying': 'กำลังเปิดหน้าชำระเงิน…',
  // Ten minutes, fixed by ReservePay. Driven from `expires_at`, never a constant.
  'q8.expiresIn': 'ต้องชำระภายใน {minutes} นาที',
  'q8.expiresSoon': 'เหลือเวลาไม่ถึง 1 นาที',
  'q8.nothingToPay': 'บิลนี้ชำระครบแล้ว',
  'q8.resume': 'กลับไปหน้าชำระเงินที่ค้างอยู่',
  'q8.backToBill': 'กลับไปดูบิล',
  'q8.payAtCounterInstead': 'หรือชำระที่เคาน์เตอร์ก็ได้',
  // A deployment with no ReservePay configuration answers 501. Honest dead end,
  // no retry spinner — the same decision 03 §Q9 made for a venue-paused venue.
  'q8.unavailableTitle': 'ร้านนี้ยังไม่เปิดรับชำระเงินผ่าน QR',
  // The server returned no page to send the guest to. Not a crash, not a retry.
  'q8.noRedirectTitle': 'ยังเปิดหน้าชำระเงินไม่ได้',
  // `upstream_timeout`: ReservePay did not answer and a checkout may or may not
  // exist. The contract says do not retry, so this screen must not offer one —
  // a second attempt is how one bill gets two checkouts a guest can pay twice.
  'q8.reconcileTitle': 'ไม่ทราบผลการเปิดรายการชำระเงิน',
  'q8.reconcileBody': 'อย่ากดซ้ำ แจ้งพนักงานเพื่อตรวจสอบก่อน',
  // The server prices the bill and refuses an amount that is not the balance.
  'q8.amountChanged': 'ยอดบิลเปลี่ยนไปแล้ว ตรวจยอดใหม่อีกครั้ง',

  // --- Q9 · payment verification ------------------------------------------
  'q9.title': 'ผลการชำระเงิน',
  'q9.checking': 'กำลังตรวจสอบ…',
  // Pandan, and only here: the server settled the bill (foundations §4).
  'q9.verifiedTitle': 'ชำระเงินเรียบร้อย',
  // The body of the green card is not decoration — it is the one instruction
  // that lets a guest put the phone away. `ไม่ต้องแสดงสลิป` ("no need to show a
  // slip") is why they can stop holding the screen up at the counter, and Q9's
  // own header comment names it as the payload this card reproduces. It went
  // missing when the card was rebuilt and the comment did not; restored here.
  //
  // Naming ReservePay in body copy is the *processor disclosure* D-014 permits,
  // and it is doing work rather than branding: it says who confirmed, which is
  // the reason the guest is free of the slip. Body copy, never brand-weighted,
  // never a lockup.
  'q9.verifiedBody': 'ร้านได้รับการยืนยันจาก ReservePay แล้ว ไม่ต้องแสดงสลิป',
  // The state a successful payment actually lands in today, because settlement
  // has no honest actor server-side. There is no tone meaning "probably paid",
  // so this says what is true and what to do — it does not reassure.
  'q9.unconfirmedTitle': 'ยังยืนยันการชำระเงินไม่ได้',
  'q9.unconfirmedBody': 'ถ้าจ่ายแล้ว แสดงสลิปที่เคาน์เตอร์ ร้านจะตรวจสอบและปิดบิลให้',
  'q9.paymentTakenTitle': 'ReservePay ยืนยันการชำระเงินแล้ว',
  'q9.paymentTakenBody': 'ร้านยังไม่ได้ปิดบิล แสดงหน้านี้ที่เคาน์เตอร์ได้เลย',
  'q9.failedTitle': 'ชำระเงินไม่สำเร็จ',
  'q9.failedBody': 'ลองชำระใหม่ หรือชำระที่เคาน์เตอร์',
  'q9.expiredTitle': 'หมดเวลาชำระเงินแล้ว',
  'q9.expiredBody': 'เริ่มรายการชำระเงินใหม่ได้เลย',
  'q9.payAgain': 'ชำระเงินอีกครั้ง',
  'q9.noAttempt': 'ยังไม่มีรายการชำระเงิน',
  'q9.recheck': 'ตรวจสอบอีกครั้ง',
  'q9.startedAt': 'เริ่มชำระเมื่อ {time}',
  // The Q14 notice. A guest who came back from ReservePay lands on `/status`,
  // because that is what `POKPOK_GUEST_RETURN_URL` is, and nothing on that URL
  // says a payment happened.
  'q9.returnNotice': 'มีรายการชำระเงิน {amount} รอยืนยัน',
  'q9.returnNoticeAction': 'ดูผลการชำระเงิน',
  // PromptPay refunds are not supported by ReservePay (01-pos.md §D10) and a
  // guest-initiated refund has no designed path anywhere. Flagged, not invented:
  // the only true thing to say is that a human handles it.
  'q9.refundNote': 'ขอคืนเงินได้ที่เคาน์เตอร์เท่านั้น',

  // --- Q12 · queue-ahead landing (counter / poster QR) --------------------
  'q12.title': 'สั่งล่วงหน้า',
  // Must not be confused with the table entry (03 §Q12): no table identity here.
  'q12.takeawayNote': 'สั่งล่วงหน้า แล้วรับที่เคาน์เตอร์',
  'q12.startOrdering': 'เริ่มสั่ง',
  'q12.pickupNote': 'สั่งเสร็จแล้วรอเรียกคิวที่เคาน์เตอร์',
  'q12.queueLength': 'ตอนนี้มี {count} คิว',
  // No endpoint gives a guest credential the queue length or a wait estimate.
  // Until one does, this surface says it does not know rather than guessing.
  'q12.queueUnknown': 'ยังไม่ทราบจำนวนคิวตอนนี้',
  'q12.waitEstimate': 'รอประมาณ {minutes} นาที',
  'q12.waitUnknown': 'ยังไม่ทราบเวลารอ',

  // --- Q14 · order status (dine-in) ---------------------------------------
  'q14.title': 'สถานะอาหาร',
  'q14.noOrder': 'ยังไม่มีออเดอร์',
  'q14.noOrderAction': 'ไปดูเมนู',
  // Prep states, not money. "Ready" is never green (foundations §4).
  'q14.statePending': 'รอครัวเริ่ม',
  'q14.stateFired': 'ครัวรับแล้ว',
  'q14.stateInProgress': 'กำลังทำ',
  'q14.stateReady': 'พร้อมเสิร์ฟ',
  'q14.stateServed': 'เสิร์ฟแล้ว',
  'q14.stateCancelled': 'ยกเลิกแล้ว',
  'q14.received': 'ร้านรับออเดอร์แล้ว',
  // The accessible name of the four-step rail. The rail is a list, and a list a
  // screen reader announces without a name is four words with no subject.
  'q14.progressLabel': 'ความคืบหน้าของออเดอร์',
  'q14.elapsed': 'ผ่านไป {minutes} นาที',
  'q14.perItemHeading': 'รายการอาหาร',
  // Honest: if the KDS is degraded and the status is stale, say so rather than
  // showing a frozen progress bar (03 §Q14).
  'q14.staleTitle': 'สถานะอาจไม่ใช่ล่าสุด',
  'q14.staleBody': 'ยังเชื่อมต่อครัวไม่ได้ตอนนี้ สอบถามพนักงานได้เลย',
  'q14.billLater': 'จ่ายทีหลังที่เคาน์เตอร์',
  'q14.orderAgain': 'สั่งเพิ่ม',
  'q14.orderCancelled': 'ออเดอร์นี้ถูกยกเลิก',
  'q14.orderVoided': 'ออเดอร์นี้ถูกยกเลิกโดยร้าน',

  // --- Q14 · the two asks ---------------------------------------------------
  // Added 2026-08-16. The screen told a guest what the kitchen was doing and
  // gave them no way to say anything back — so the only channel was standing up
  // and waving, which is the thing a QR surface exists to remove.
  'q14.callStaff': 'เรียกพนักงาน',
  'q14.callStaffSent': 'เรียกพนักงานแล้ว กำลังไปที่โต๊ะ',
  // Never "somebody is coming": what the server knows is that the ask was
  // recorded. A second press is not an error and not a second call.
  'q14.callStaffPending': 'เรียกพนักงานแล้ว',
  'q14.requestBill': 'เรียกเก็บเงิน',
  'q14.requestBillSent': 'เรียกเก็บเงินแล้ว พนักงานจะมาที่โต๊ะ',
  'q14.requestBillDone': 'เรียกเก็บเงินแล้ว',
  'q14.askFailed': 'ส่งไม่สำเร็จ ลองอีกครั้ง',

  // --- Q17 · order confirmation & receipt ---------------------------------
  'q17.title': 'สรุปออเดอร์',
  'q17.thanks': 'ขอบคุณ',
  'q17.orderNumber': 'ออเดอร์ {number}',
  'q17.placedAt': 'สั่งเมื่อ {time}',
  'q17.itemsHeading': 'รายการ',
  // Pandan only when the server says the money is verified — order `settled` or
  // `completed`, or a payment in state `succeeded`. Nothing else.
  'q17.paid': 'เงินเข้าแล้ว',
  'q17.paidAt': 'ชำระเมื่อ {time}',
  'q17.unpaid': 'ยังไม่ได้ชำระ',
  'q17.balanceDue': 'ยอดค้างชำระ',
  'q17.payAtCounter': 'ชำระที่เคาน์เตอร์',
  // The one door into Q8. Offered beside paying at the counter, never instead of
  // it: nothing in the contract says whether a venue is order-first or pay-first
  // (proposal C-2 is deferred, D-024), so this surface may not decide for it.
  'q17.payOnline': 'ชำระเงินออนไลน์',
  'q17.refunded': 'คืนเงินแล้ว',
  'q17.partiallyRefunded': 'คืนเงินบางส่วนแล้ว',
  'q17.venueHeading': 'ร้าน',
  // UNUSED, deliberately. The receipt cannot keep this promise: reading an order
  // needs a guest session token, and that token is table-scoped and short-lived,
  // so a guest who comes back later gets `error.session` instead. Q17 renders
  // nothing in its place rather than a claim it cannot honour. Kept for the day
  // a durable receipt link exists to promise.
  'q17.keepThisPage': 'เปิดหน้านี้ดูใหม่ได้ตลอด',
  // A guest-facing tax invoice is not a thing this surface can issue: the e-Tax
  // engine is V2 and there is no endpoint for it. Point at the counter.
  'q17.taxInvoice': 'ขอใบกำกับภาษี',
  'q17.taxInvoiceAtCounter': 'ขอใบกำกับภาษีได้ที่เคาน์เตอร์',
  'q17.notFound': 'ไม่พบออเดอร์นี้',
  'q17.notFoundAction': 'สอบถามพนักงานได้เลย',
  // Processor disclosure, and only that (D-014, foundations §7). Factual, small,
  // body copy. Never a lockup, never brand-weighted, never near the wordmark.
  'q17.processorDisclosure': 'ชำระเงินผ่าน ReservePay',

  // --- Q19 · expired / regenerated / invalid QR ---------------------------
  // Table QRs are regenerable if abused, so this is a designed-for state, not an
  // exception. One line, and it says what to do.
  'q19.invalidTitle': 'QR นี้ใช้ไม่ได้',
  'q19.expiredTitle': 'QR นี้หมดอายุแล้ว',
  'q19.regeneratedTitle': 'QR ของโต๊ะนี้เปลี่ยนใหม่แล้ว',
  'q19.body': 'สแกน QR บนโต๊ะอีกครั้ง หรือขอ QR ใหม่จากพนักงาน',
  'q19.rescan': 'สแกนใหม่',
  'q19.noQrTitle': 'ยังไม่ได้สแกน QR',
  'q19.noQrBody': 'เปิดหน้านี้ได้จากการสแกน QR บนโต๊ะเท่านั้น',

  // --- Q20 · venue closed / not accepting orders --------------------------
  // Not an error. A menu should still be browsable if the venue allows it.
  'q20.closedTitle': 'ร้านปิดอยู่',
  'q20.outsideHoursTitle': 'ยังไม่ถึงเวลาเปิด',
  // DECIDED (design, 2026-08): venue-paused is an honest dead end. No retry
  // spinner, no queue, no "try again shortly" that resolves into nothing.
  'q20.notAcceptingTitle': 'ตอนนี้ยังไม่รับออเดอร์ผ่าน QR',
  'q20.notAcceptingBody': 'สั่งกับพนักงานได้เลย',
  'q20.reopensAt': 'เปิดอีกครั้ง {time}',
  'q20.reopensUnknown': 'ยังไม่ระบุเวลาเปิด',
  'q20.hoursHeading': 'เวลาเปิด-ปิด',
  'q20.hoursToday': 'วันนี้ {hours}',
  'q20.hoursUnknown': 'ยังไม่ระบุเวลาเปิด-ปิด',
  'q20.browseAnyway': 'ดูเมนูไว้ก่อนได้',
  'q20.day.mon': 'จันทร์',
  'q20.day.tue': 'อังคาร',
  'q20.day.wed': 'พุธ',
  'q20.day.thu': 'พฤหัสบดี',
  'q20.day.fri': 'ศุกร์',
  'q20.day.sat': 'เสาร์',
  'q20.day.sun': 'อาทิตย์',

  // --- booking · customer web (BookWeb / BookDone / BookManage) ------------
  // A public flow reached from a booking link, not a scanned QR. Voice is the
  // same neighbour-next-door register; the cancel window is stated plainly.
  'book.title': 'จองโต๊ะ',
  'book.lead': 'เลือกวันเวลา แล้วกรอกข้อมูลติดต่อ',
  'book.dateLabel': 'วันที่',
  'book.timeLabel': 'เวลา',
  'book.partyLabel': 'จำนวนคน',
  'book.nameLabel': 'ชื่อผู้จอง',
  'book.namePlaceholder': 'ชื่อที่ใช้เรียก',
  'book.phoneLabel': 'เบอร์มือถือ',
  'book.phonePlaceholder': '08x-xxx-xxxx',
  'book.today': 'วันนี้',
  'book.pickDate': 'เลือกวันที่ {date}',
  'book.pickTime': 'เลือกเวลา {time}',
  'book.partyDecrease': 'ลดจำนวนคน',
  'book.partyIncrease': 'เพิ่มจำนวนคน',
  'book.partyValueLabel': 'จำนวน {count} คน',
  'book.people': '{count} คน',
  'book.summary': '{date} {time} · {count} คน',
  'book.holdNote': 'ทางร้านจะยืนยันการจองอีกครั้ง',
  'book.submit': 'ยืนยันการจอง',
  'book.submitting': 'กำลังจอง…',
  'book.needName': 'ใส่ชื่อผู้จองก่อน',
  'book.needPhone': 'ใส่เบอร์มือถือก่อน',
  'book.needTime': 'เลือกเวลาก่อน',
  'book.doneTitle': 'จองเรียบร้อย',
  'book.doneBody': 'ทางร้านได้รับการจองของคุณแล้ว',
  'book.detailsHeading': 'รายละเอียดการจอง',
  'book.fieldDate': 'วันที่',
  'book.fieldTime': 'เวลา',
  'book.fieldParty': 'จำนวน',
  'book.fieldName': 'ชื่อผู้จอง',
  'book.fieldPhone': 'เบอร์มือถือ',
  'book.fieldReference': 'รหัสจอง',
  'book.fieldStatus': 'สถานะ',
  'book.saveLinkNote': 'บันทึกลิงก์หน้านี้ไว้ เพื่อดูหรือยกเลิกการจองภายหลัง',
  'book.manageAction': 'จัดการการจอง',
  'book.manageTitle': 'การจอง',
  'book.manageTitleRef': 'การจอง {reference}',
  'book.statusPending': 'รอยืนยัน',
  'book.statusConfirmed': 'ยืนยันแล้ว',
  'book.statusSeated': 'เข้านั่งแล้ว',
  'book.statusCompleted': 'เสร็จสิ้น',
  'book.statusCancelled': 'ยกเลิกแล้ว',
  'book.statusNoShow': 'ไม่มาตามนัด',
  'book.statusUnknown': 'สถานะไม่ทราบ',
  'book.cancelHeading': 'ยกเลิกการจอง',
  'book.cancelNote': 'ยกเลิกได้ฟรีก่อนถึงเวลาจอง 2 ชั่วโมง หลังจากนั้นโปรดโทรแจ้งร้าน',
  'book.cancelAction': 'ยกเลิกการจอง',
  'book.cancelling': 'กำลังยกเลิก…',
  'book.cancelConfirmTitle': 'ยกเลิกการจองนี้?',
  'book.cancelConfirmBody': 'ยกเลิกแล้วเปลี่ยนกลับไม่ได้',
  'book.cancelConfirmYes': 'ยืนยันยกเลิก',
  'book.keepBooking': 'เก็บการจองไว้',
  'book.cancelledTitle': 'ยกเลิกการจองแล้ว',
  'book.cancelledBody': 'ทางร้านได้รับการยกเลิกแล้ว',
  'book.cannotCancelWindow': 'ใกล้ถึงเวลาจองแล้ว (ภายใน 2 ชั่วโมง) ยกเลิกเองไม่ได้ โปรดโทรแจ้งร้าน',
  'book.cannotCancelTerminal': 'การจองนี้ยกเลิกไม่ได้แล้ว',
  'book.cancelWindowError': 'ยกเลิกไม่ทันแล้ว โปรดโทรแจ้งร้าน',
  'book.notFoundTitle': 'ไม่พบการจองนี้',
  'book.notFoundBody': 'ลิงก์อาจหมดอายุหรือไม่ถูกต้อง สอบถามร้านได้เลย',
  // --- Feedback · post-bill rating ----------------------------------------
  // A whole 1–5 stars (the scale the frozen contract fixes) plus an optional
  // comment. PDPA: the hint says no name or number is needed, so a guest does
  // not volunteer one.
  'feedback.title': 'มื้อนี้เป็นอย่างไรบ้าง',
  'feedback.subtitle': 'ให้คะแนนและบอกเล่าให้ร้านฟังได้เลย',
  'feedback.ratingLabel': 'ให้คะแนน',
  // {count} is the star number, 1–5 — the accessible name of each star.
  'feedback.starLabel': '{count} ดาว',
  'feedback.commentLabel': 'ความคิดเห็น (ไม่บังคับ)',
  'feedback.commentHint': 'ไม่ต้องใส่ชื่อหรือเบอร์โทร',
  'feedback.commentPlaceholder': 'อยากบอกอะไรร้านเพิ่มเติมไหม',
  'feedback.ratingRequired': 'เลือกจำนวนดาวก่อนส่ง',
  'feedback.submit': 'ส่งความคิดเห็น',
  'feedback.submitting': 'กำลังส่ง…',
  'feedback.failedTitle': 'ส่งความคิดเห็นไม่สำเร็จ',
  'feedback.failedAction': 'ลองอีกครั้งได้เลย',
  'feedback.sentTitle': 'ขอบคุณสำหรับความคิดเห็น',
  'feedback.sentBody': 'ร้านได้รับความคิดเห็นของคุณแล้ว',
} as const;
